import { useState, useEffect } from "react";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { 
  Download, 
  Smartphone, 
  CheckCircle2, 
  Share, 
  MoreVertical,
  Plus,
  ArrowDown
} from "lucide-react";

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}

export default function InstallApp() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Detect platform
    const ua = navigator.userAgent.toLowerCase();
    setIsIOS(/iphone|ipad|ipod/.test(ua));
    setIsAndroid(/android/.test(ua));

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for app installed
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setDeferredPrompt(null);
    }
  };

  if (isInstalled) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500/10 mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
            <h2 className="text-xl font-semibold mb-2">App Installed!</h2>
            <p className="text-muted-foreground">
              You can now access Robotic Imaging from your home screen.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/10 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-primary/10 ring-1 ring-primary/20 mx-auto mb-4">
            <Smartphone className="h-10 w-10 text-primary" />
          </div>
          <CardTitle className="text-2xl">Install Robotic Imaging</CardTitle>
          <CardDescription>
            Add to your home screen for quick access and offline support
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Benefits */}
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
              <span className="text-sm">Works offline - access your data anywhere</span>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
              <span className="text-sm">Fast loading - opens instantly from home screen</span>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
              <span className="text-sm">Full screen experience - no browser bars</span>
            </div>
          </div>

          {/* Install Button or Instructions */}
          {deferredPrompt ? (
            <Button 
              onClick={handleInstallClick} 
              className="w-full h-12 text-base"
            >
              <Download className="h-5 w-5 mr-2" />
              Install App
            </Button>
          ) : isIOS ? (
            <div className="space-y-4">
              <p className="text-sm text-center text-muted-foreground font-medium">
                To install on iOS:
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 rounded-lg border">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                    <Share className="h-4 w-4 text-primary" />
                  </div>
                  <span className="text-sm">1. Tap the <strong>Share</strong> button</span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                    <ArrowDown className="h-4 w-4 text-primary" />
                  </div>
                  <span className="text-sm">2. Scroll down in the menu</span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                    <Plus className="h-4 w-4 text-primary" />
                  </div>
                  <span className="text-sm">3. Tap <strong>Add to Home Screen</strong></span>
                </div>
              </div>
            </div>
          ) : isAndroid ? (
            <div className="space-y-4">
              <p className="text-sm text-center text-muted-foreground font-medium">
                To install on Android:
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 rounded-lg border">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                    <MoreVertical className="h-4 w-4 text-primary" />
                  </div>
                  <span className="text-sm">1. Tap the <strong>menu icon</strong> (⋮)</span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                    <Download className="h-4 w-4 text-primary" />
                  </div>
                  <span className="text-sm">2. Tap <strong>Install app</strong> or <strong>Add to Home screen</strong></span>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-sm text-muted-foreground">
              <p>Open this page on your mobile device to install the app.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
